import React, { useState, useEffect } from 'react';
import { startOfMonth, endOfMonth, format, differenceInDays } from 'date-fns';
import { Calendar, Download } from 'lucide-react';
import { getUsageHistory } from '../services/api/usage';
import { UsageStats } from '../types';
import UsageChart from '../components/analytics/UsageChart';
import UsageDistributionChart from '../components/analytics/UsageDistributionChart';
import UsageTable from '../components/analytics/UsageTable';
import { bots } from '../data/bots';
import { createLogger } from '../utils/logger';

const logger = createLogger('analytics-page');

const Analytics: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
  });
  const [usageData, setUsageData] = useState<UsageStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBot, setSelectedBot] = useState<string>('all');

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await getUsageHistory(
          format(dateRange.startDate, 'yyyy-MM-dd'),
          format(dateRange.endDate, 'yyyy-MM-dd')
        );

        if (!response.success) {
          throw new Error('Failed to fetch usage data');
        }

        setUsageData(response.usage || []);
      } catch (error) {
        logger.error('Error fetching usage data', error);
        setError('Failed to fetch usage data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsageData();
  }, [dateRange]);

  const filteredData = usageData.filter(
    item => selectedBot === 'all' || item.botId === selectedBot
  );

  // Calculate total cost based on bot prices
  const calculateTotalCost = (data: UsageStats[]): number => {
    return data.reduce((sum, item) => sum + item.creditsUsed, 0);
  };

  const totalCost = calculateTotalCost(filteredData);
  const totalUses = filteredData.length;

  // Determine period based on date range
  const daysDifference = differenceInDays(dateRange.endDate, dateRange.startDate);
  const period = daysDifference <= 31 ? 'day' : daysDifference <= 90 ? 'week' : 'month';

  const downloadCsv = () => {
    const headers = ['Date', 'Bot', 'Credits Used'];
    const rows = filteredData.map(usage => [
      format(new Date(usage.timestamp), 'yyyy-MM-dd HH:mm:ss'),
      usage.botTitle || 'Unknown Bot',
      usage.creditsUsed.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usage-${format(dateRange.startDate, 'yyyy-MM-dd')}-to-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="py-8">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-gray-500" />
            <input
              type="date"
              value={format(dateRange.startDate, 'yyyy-MM-dd')}
              onChange={(e) => setDateRange(prev => ({
                ...prev,
                startDate: new Date(e.target.value)
              }))}
              className="text-sm border-gray-300 rounded-md"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={format(dateRange.endDate, 'yyyy-MM-dd')}
              onChange={(e) => setDateRange(prev => ({
                ...prev,
                endDate: new Date(e.target.value)
              }))}
              className="text-sm border-gray-300 rounded-md"
            />
          </div>
          <select
            value={selectedBot}
            onChange={(e) => setSelectedBot(e.target.value)}
            className="text-sm border-gray-300 rounded-md"
          >
            <option value="all">All Bots</option>
            {bots.map(bot => (
              <option key={bot.id} value={bot.id}>{bot.title}</option>
            ))}
            {bots.flatMap(bot => bot.subBots || []).map(subBot => (
              <option key={subBot.id} value={subBot.id}>{subBot.title}</option>
            ))}
          </select>
          <button
            onClick={downloadCsv}
            className="flex items-center space-x-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {error ? (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
          {error}
        </div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-500 mb-2">Total Spent</h3>
              <p className="text-3xl font-bold text-gray-900">${totalCost.toFixed(2)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-500 mb-2">Total Uses</h3>
              <p className="text-3xl font-bold text-gray-900">{totalUses}</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-medium text-gray-500 mb-2">Average Cost per Use</h3>
              <p className="text-3xl font-bold text-gray-900">
                ${totalUses > 0 ? (totalCost / totalUses).toFixed(2) : '0.00'}
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Usage Chart */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Over Time</h2>
              <UsageChart data={filteredData} period={period} />
            </div>

            {/* Distribution Chart */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Distribution</h2>
              <UsageDistributionChart data={filteredData} />
            </div>
          </div>

          {/* Usage Table */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Usage Details</h2>
            <UsageTable data={filteredData} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;